import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import hrService from '../../../services/hrService';

const AdminNotificationCenter = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadNotifications();
  }, []);

  const loadNotifications = async () => {
    setLoading(true);
    setError('');
    try {
      // Get recent admin actions as notifications
      const adminActions = await hrService?.getAdminActionsLog({ limit: 10 });
      
      // Get pending leave requests as notifications
      const pendingLeaves = await hrService?.getLeaveRequests({ status: 'pending' });
      
      // Combine and format as notifications
      const combinedNotifications = [];
      
      // Add recent admin actions
      adminActions?.forEach(action => {
        combinedNotifications?.push({
          id: `action_${action?.id}`,
          type: 'admin_action',
          priority: 'low',
          message: `${action?.admin?.fullName || 'Admin'} performed ${action?.actionType?.replace('_', ' ')}`,
          details: action?.targetEmployee?.userProfiles?.fullName ? `for ${action?.targetEmployee?.userProfiles?.fullName}` : '',
          createdAt: action?.createdAt,
          isRead: true
        });
      });
      
      // Add pending leave requests as high priority notifications
      pendingLeaves?.forEach(leave => {
        combinedNotifications?.push({
          id: `leave_${leave?.id}`,
          type: 'leave_request',
          priority: 'high',
          message: `New leave request from ${leave?.employees?.userProfiles?.fullName || 'Employee'}`,
          details: `${leave?.leaveType?.replace('_', ' ')} for ${leave?.totalDays} days`,
          createdAt: leave?.createdAt,
          isRead: false
        });
      });
      
      // Sort by created date, unread first
      combinedNotifications?.sort((a, b) => {
        if (a?.isRead !== b?.isRead) {
          return a?.isRead ? 1 : -1;
        }
        return new Date(b?.createdAt) - new Date(a?.createdAt);
      });
      
      setNotifications(combinedNotifications?.slice(0, 15));
    } catch (err) {
      console.error('Error loading admin notifications:', err);
      setError('Failed to load notifications');
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    setNotifications(prevNotifications =>
      prevNotifications?.map(notif =>
        notif?.id === notificationId ? { ...notif, isRead: true } : notif
      )
    );
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'leave_request': return 'Calendar';
      case 'admin_action': return 'Settings';
      case 'attendance_alert': return 'Clock';
      case 'employee_milestone': return 'Award';
      case 'payroll_reminder': return 'DollarSign';
      case 'document_expiry': return 'FileX';
      default: return 'Bell';
    }
  };

  const getNotificationColor = (type, priority) => {
    if (priority === 'high') return 'destructive';
    if (priority === 'medium') return 'warning';
    
    switch (type) {
      case 'leave_request': return 'primary';
      case 'admin_action': return 'success';
      case 'attendance_alert': return 'warning';
      case 'employee_milestone': return 'success';
      case 'payroll_reminder': return 'warning';
      case 'document_expiry': return 'destructive';
      default: return 'primary';
    }
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays}d ago`;
  };

  const renderLoadingState = () => (
    <div className="space-y-3">
      {[1, 2, 3]?.map((i) => (
        <div
          key={i}
          className="flex items-start space-x-3 p-3 rounded-lg border border-border animate-pulse"
        >
          <div className="w-8 h-8 bg-muted rounded-full"></div>
          <div className="flex-1">
            <div className="w-3/4 h-4 bg-muted rounded mb-2"></div>
            <div className="w-1/2 h-3 bg-muted rounded"></div>
          </div>
          <div className="w-12 h-3 bg-muted rounded"></div>
        </div>
      ))}
    </div>
  );

  const renderEmptyState = () => (
    <div className="text-center py-8">
      <Icon name="Bell" size={48} color="var(--color-muted)" className="mx-auto mb-4" />
      <p className="text-muted-foreground mb-2">No notifications</p>
      <p className="text-sm text-muted-foreground">
        System notifications and alerts will appear here
      </p>
    </div>
  );

  const renderErrorState = () => (
    <div className="text-center py-8">
      <Icon name="AlertCircle" size={48} color="var(--color-destructive)" className="mx-auto mb-4" />
      <p className="text-destructive mb-2">Failed to load notifications</p>
      <p className="text-sm text-muted-foreground mb-4">{error}</p>
      <button
        onClick={loadNotifications}
        className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
      >
        Try Again
      </button>
    </div>
  );

  return (
    <div className="bg-card rounded-lg border border-border card-shadow p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <h2 className="text-xl font-semibold text-foreground">Notifications</h2>
          {notifications?.filter(n => !n?.isRead)?.length > 0 && (
            <span className="bg-destructive text-destructive-foreground text-xs px-2 py-1 rounded-full">
              {notifications?.filter(n => !n?.isRead)?.length}
            </span>
          )}
        </div>
        <button
          onClick={loadNotifications}
          disabled={loading}
          className="p-2 rounded-md hover:bg-background/50 transition-colors disabled:opacity-50"
        >
          <Icon 
            name={loading ? "Loader2" : "RefreshCw"} 
            size={18} 
            className={loading ? "animate-spin" : ""}
          />
        </button>
      </div>

      {loading ? (
        renderLoadingState()
      ) : error ? (
        renderErrorState()
      ) : notifications?.length === 0 ? (
        renderEmptyState()
      ) : (
        <div className="space-y-3 max-h-96 overflow-y-auto">
          {notifications?.map((notification) => {
            const color = getNotificationColor(notification?.type, notification?.priority);
            
            return (
              <div
                key={notification?.id}
                className={`
                  flex items-start space-x-3 p-3 rounded-lg border transition-colors cursor-pointer
                  ${notification?.isRead 
                    ? 'border-border bg-background/50' :'border-primary/20 bg-primary/5'
                  }
                  hover:bg-background/80
                `}
                onClick={() => !notification?.isRead && markAsRead(notification?.id)}
              >
                <div className={`
                  w-8 h-8 rounded-full flex items-center justify-center
                  ${color === 'destructive' ? 'bg-red-100' : ''}
                  ${color === 'warning' ? 'bg-yellow-100' : ''}
                  ${color === 'success' ? 'bg-green-100' : ''}
                  ${color === 'primary' ? 'bg-primary/10' : ''}
                `}>
                  <Icon 
                    name={getNotificationIcon(notification?.type)} 
                    size={16} 
                    color={`var(--color-${color})`}
                  />
                </div>
                
                <div className="flex-1 min-w-0">
                  <p className={`
                    text-sm mb-1 leading-relaxed
                    ${notification?.isRead ? 'text-muted-foreground' : 'text-foreground font-medium'}
                  `}>
                    {notification?.message}
                  </p>
                  
                  {notification?.details && (
                    <p className="text-xs text-muted-foreground mb-1">
                      {notification?.details}
                    </p>
                  )}
                  
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-muted-foreground">
                      {formatTimeAgo(notification?.createdAt)}
                    </p>
                    
                    {notification?.priority && (
                      <span className={`
                        text-xs px-2 py-0.5 rounded-full
                        ${notification?.priority === 'high' ? 'bg-red-100 text-red-600' : ''}
                        ${notification?.priority === 'medium' ? 'bg-yellow-100 text-yellow-600' : ''}
                        ${notification?.priority === 'low' ? 'bg-green-100 text-green-600' : ''}
                      `}>
                        {notification?.priority}
                      </span>
                    )}
                  </div>
                </div>
                
                {!notification?.isRead && (
                  <div className="w-2 h-2 bg-primary rounded-full mt-2"></div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminNotificationCenter;